/**
 * Flush whatever editor is currently mounted, whichever engine it is.
 *
 * ## The bug this exists to kill
 *
 * Every deal-flow transition — send to counterparty, mark ready to sign,
 * request review, grant access, prepare to sign — must land the user's most
 * recent edits BEFORE it fires its mutation, because the server immediately
 * reads both the body and the `document_suggestions` gates.
 *
 * The CKEditor era did this inline, everywhere:
 *
 *     if (window.editor?.plugins?.has('Autosave')) {
 *       await window.editor.plugins.get('Autosave').save();
 *     }
 *
 * `window.editor` is set only by CKEditor. Under the DOCX-native editor that
 * condition is simply false, so ~10 call sites stopped flushing and nobody
 * noticed: the guard fails SILENTLY and the mutation proceeds. Edits made
 * inside the autosave debounce were transmitted as though they never happened,
 * and — worse — tracked changes made in that window never reached
 * `document_suggestions`, so the pending-suggestion gates saw zero.
 *
 * This is the one place that knows how to flush each engine. Call sites state
 * intent ("flush before I transition") instead of naming an engine, so a future
 * engine is one branch here rather than another silent no-op sprayed across the
 * codebase.
 */

import { getDocxNativeEditor } from './docx-native/docxNativeEditorRegistry';

export interface FlushResult {
  /**
   * PENDING suggestions after the flush reconciled the relational rows.
   * `null` when the engine can't report it (CKEditor), in which case callers
   * must fall back to the server's own gate rather than assuming zero.
   */
  pendingSuggestions: number | null;
}

/**
 * @param documentId The document whose editor should be flushed. Required to
 *                   find the DOCX-native handle (the registry is keyed by id).
 */
export async function flushActiveEditor(
  documentId?: string | number | null
): Promise<FlushResult> {
  // DOCX-native: the container registers a handle that both saves and
  // reconciles the suggestion table, and tells us the resulting count.
  if (documentId != null) {
    const handle = getDocxNativeEditor(String(documentId));
    if (handle?.flushPendingChanges) {
      try {
        const { pendingSuggestions } = await handle.flushPendingChanges();
        return { pendingSuggestions };
      } catch {
        // A failed flush must not block the transition outright — the server
        // still enforces its own gates. Report "unknown" so callers don't read
        // it as "clean".
        return { pendingSuggestions: null };
      }
    }
  }

  // CKEditor (clause authoring still mounts one): flush its autosave. It has no
  // suggestion-count concept, so the count is unknown.
  const cke = (window as unknown as { editor?: any }).editor;
  if (cke?.plugins?.has?.('Autosave')) {
    try {
      await cke.plugins.get('Autosave').save();
    } catch {
      /* same rationale as above */
    }
  }

  return { pendingSuggestions: null };
}
